import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import TypeaheadSelectBootstrap5 from '../../../../components/react/autocompletes/typeahead_select_bootstrap_5';
import {
  AlertHandler,
  SuccessAlert,
} from '../../../../components/react/alert_handler/alert_handler';
import NewsShowContext from '../../../context/news_show_context';
import { sendEmailArticle } from '../../services/sharing_tokens.api.service';
import Tooltip from '../../../../components/react/tooltip/tooltip';

const ShareEmailModal = ({ isShareEmailModalOpen, closeShareEmailModal }) => {
  const { articleId } = useContext(NewsShowContext);
  const [showEmailError, setShowEmailError] = useState(false);
  const [textMessage, setTextMessage] = useState('');
  const [emailAddresses, setEmailAddresses] = useState([]);
  const [isFormValid, setIsFormValid] = useState(false);

  /**
   * @param {Array.<{text: string, id: number, customOption: boolean}>} selected - List of entries
   * @returns {undefined}
   */
  const onEmailSelection = (selected) => {
    setIsFormValid(!!selected.length);
    setEmailAddresses(selected);
  };

  const alertHandler = new AlertHandler();

  const sendEmail = () => {
    const shareByEmail = {
      email_addresses: emailAddresses.map((email) => email.text),
      message: textMessage,
    };

    sendEmailArticle(articleId, shareByEmail)
      .then(() => {
        setTextMessage('');
        setEmailAddresses([]);
        alertHandler.render(SuccessAlert, 'You have successfully emailed this article.');
        closeShareEmailModal();
      })
      .catch(() => {
        setShowEmailError(true);
      });
  };

  const hideModal = () => {
    setShowEmailError(false);
    setTextMessage('');
    setEmailAddresses([]);
    closeShareEmailModal();
  };

  /**
   * @type {JSX.Element}
   */
  const renderSendButton = (
    <button onClick={sendEmail} className='btn btn-outline-primary' disabled={!isFormValid}>
      Send
    </button>
  );

  /**
   * @param {string} input - User input as string
   * @returns {undefined}
   */
  const handleInputChange = (input) => {
    setIsFormValid(!input.length);
  };

  return (
    <Modal data-testid='share-email-modal' show={isShareEmailModalOpen} onHide={hideModal}>
      <Modal.Header closeButton>
        <h2 className='modal-title'>Send article by email</h2>
      </Modal.Header>
      <Modal.Body>
        <p className='text-gray-li my-2'>
          Share this article with your team by email. They will be able to view it even if they
          don‘t have a Devex membership.
        </p>
        <div className='my-4' data-testid='email-addresses'>
          <label className='mb-2 strong'>Emails</label>
          <TypeaheadSelectBootstrap5
            id='email-addresses'
            inputProps={{
              className: 'form-control',
              'aria-label': 'Email addresses',
            }}
            allowNew={true}
            highlightOnlyResult={false}
            clearButton={true}
            multiple={true}
            newSelectionPrefix='Add email: '
            options={[]}
            placeholder='Type an email'
            onChange={onEmailSelection}
            selected={emailAddresses || []}
            onInputChange={handleInputChange}
            emptyLabel='Please add an email'
          />
        </div>
        <label className='mb-2 strong'>Message</label>
        <textarea
          className='form-control'
          aria-label='Message'
          value={textMessage}
          onChange={(e) => setTextMessage(e.target.value)}
        />
        {showEmailError && (
          <p data-testid='error-message' className='alert alert-danger mb-0 pl-3 pr-3'>
            <i className='fal icon-times-circle me-2'></i>
            Error sharing article via email. Please try again later.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer className='mt-3'>
        {emailAddresses.length ? (
          renderSendButton
        ) : (
          // Note that, for some browsers (e.g. Chrome), the tooltip will not appear because it
          // considers that a disabled button should not produce any interaction. The tooltip
          // is shown in Firefox.
          <Tooltip placement='top' message={'Please add an email'}>
            {renderSendButton}
          </Tooltip>
        )}
      </Modal.Footer>
    </Modal>
  );
};

ShareEmailModal.propTypes = {
  isShareEmailModalOpen: PropTypes.bool.isRequired,
  closeShareEmailModal: PropTypes.func.isRequired,
};

export default ShareEmailModal;
